// Ubicación sugerida en tu proyecto: src/components/forms/SubcategoriaMultiSelect.tsx
// Requiere los componentes shadcn: popover, command, badge.
// Si no los tienes: npx shadcn@latest add command popover badge

import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SubcategoriaOpcion {
  id_subcategoria: string;
  subcategoria: string;
}

interface SubcategoriaMultiSelectProps {
  opciones: SubcategoriaOpcion[];
  seleccionadas: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Clase del trigger. Pásale la misma que usas en tus Inputs/SelectTrigger
   *  para que el combobox tenga exactamente el mismo alto y estilo. */
  className?: string;
}

// Estilo por defecto si no se pasa className, parecido al Button outline
// que usábamos antes (para no romper otros sitios donde ya se usa este
// componente sin pasarle una clase personalizada).
const baseTriggerClass =
  'flex h-12 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-normal shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50';

export default function SubcategoriaMultiSelect({
  opciones,
  seleccionadas,
  onChange,
  placeholder = 'Selecciona subcategorías (opcional)',
  disabled = false,
  className,
}: SubcategoriaMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) => {
    if (seleccionadas.includes(id)) {
      onChange(seleccionadas.filter((s) => s !== id));
    } else {
      onChange([...seleccionadas, id]);
    }
  };

  const quitar = (id: string) => {
    onChange(seleccionadas.filter((s) => s !== id));
  };

  const seleccionadasInfo = opciones.filter((o) =>
    seleccionadas.includes(o.id_subcategoria)
  );

  const sinOpciones = opciones.length === 0;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || sinOpciones}
            className={cn(baseTriggerClass, className)}
          >
            <span className="truncate text-left text-gray-500">
              {sinOpciones ? 'Elige primero una categoría' : placeholder}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Buscar subcategoría..." />
            <CommandList>
              <CommandEmpty>Sin resultados.</CommandEmpty>
              <CommandGroup>
                {opciones.map((op) => {
                  const isSelected = seleccionadas.includes(op.id_subcategoria);
                  return (
                    <CommandItem
                      key={op.id_subcategoria}
                      onSelect={() => toggle(op.id_subcategoria)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {op.subcategoria}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {seleccionadasInfo.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {seleccionadasInfo.map((s) => (
            <Badge key={s.id_subcategoria} variant="secondary" className="gap-1 pr-1">
              {s.subcategoria}
              <button
                type="button"
                onClick={() => quitar(s.id_subcategoria)}
                className="ml-1 rounded-full hover:bg-gray-300/50 p-0.5"
                aria-label={`Quitar ${s.subcategoria}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}